import { cloneElement, isValidElement, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHover } from "react-use";

import useTextMeasure from "../../use/text-measure";
import { classNames } from "../../utils";

export default function Tooltip({
  children,
  content,
  delay,
  show: forceShow,
}: {
  children: React.ReactNode;
  content?: string;
  delay?: boolean;
  show?: boolean;
}) {
  const [show, setShow] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const hoverableRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [align, setAlign] = useState<"center" | "left" | "right">("center");
  const [verticalAlign, setVerticalAlign] = useState<"bottom" | "top">("bottom");
  const delayMs = delay ? 1000 : 0;

  if (!isValidElement(children)) children = <span>{children}</span>;

  const [hoverable, hovered] = useHover(children);
  const trackedHoverable = cloneElement(hoverable, { ...hoverable.props, ref: hoverableRef });
  const contentLength = useTextMeasure("text-sm whitespace-pre-line", content);

  useEffect(() => {
    const show = !!content && (forceShow || hovered);
    let timeout: NodeJS.Timeout | undefined;
    if (show) {
      timeout = setTimeout(() => {
        setShow(true);
        timeout = setTimeout(() => {
          setFadeIn(true);
        }, 50);
      }, delayMs);
    } else if (!!content) {
      setFadeIn(false);
      timeout = setTimeout(() => {
        setShow(false);
      }, 200);
    } else {
      setShow(false);
      setFadeIn(false);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [forceShow, hovered, content, delayMs]);

  useEffect(() => {
    if (tooltipRef.current && hoverableRef.current) {
      const targetPosition = hoverableRef.current.getBoundingClientRect(),
        tooltipPosition = tooltipRef.current.getBoundingClientRect();
      let align: "center" | "left" | "right" = "center",
        verticalAlign: "bottom" | "top" = "top";

      if (targetPosition.left + targetPosition.width / 2 + tooltipPosition.width > window.innerWidth - 10)
        align = "right";

      if (targetPosition.left + targetPosition.width / 2 - tooltipPosition.width < 10) align = "left";

      if (targetPosition.top - tooltipPosition.height < 30) verticalAlign = "bottom";

      setHoverPosition({
        x:
          align === "center"
            ? Math.floor(targetPosition.left + targetPosition.width / 2)
            : align === "left"
              ? Math.floor(targetPosition.left)
              : Math.floor(targetPosition.right),
        y: verticalAlign === "top" ? Math.floor(targetPosition.top) : Math.floor(targetPosition.bottom),
      });
      setAlign(align);
      setVerticalAlign(verticalAlign);
    } else {
      setHoverPosition({ x: 0, y: 0 });
    }
  }, [show, hoverableRef, tooltipRef, content, fadeIn]);

  return (
    <>
      {trackedHoverable}
      {show &&
        createPortal(
          <span
            className={classNames(
              `fixed text-center flex max-w-[300px] px-2 py-1 bg-neutral-700 dark:bg-neutral-200 rounded text-white dark:text-black
              text-sm transition-opacity duration-200 z-60 whitespace-pre-line pointer-events-none`,
              verticalAlign === "top" && "translate-y-[calc(-100%_-_8px)] after:top-[100%]",
              verticalAlign === "bottom" && "translate-y-[8px] after:bottom-[100%] after:rotate-180",
              align === "center" && "after:left-1/2 -translate-x-1/2",
              align === "right" && "after:right-2 translate-x-[calc(-100%_+_13px)]",
              align === "left" && "after:left-4",
              `after:content-[''] after:absolute after:-translate-x-1/2 after:border-8 after:border-x-transparent
              after:border-b-transparent after:border-t-neutral-700 dark:after:border-t-neutral-200`,
              fadeIn ? "opacity-100" : "opacity-0",
            )}
            ref={tooltipRef}
            style={{
              left: hoverPosition.x,
              top: hoverPosition.y,
              width: contentLength + 16,
            }}
          >
            {content}
          </span>,
          document.body,
        )}
    </>
  );
}
